import { useCallback, useEffect, useMemo, useState } from "react";
import { PiggyBank, BanknoteArrowDownIcon, BanknoteArrowUpIcon, Building2, Search, TrendingUp } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchProfits } from "../../../store/PurchaseSlice";
import {
    fetchWalletsByOrganizations,
    withdrawFromProfit,
    transferProfitToCompany,
} from "../../../store/WalletSlice";
import { formatCurrencyBRL } from "../../../utils/format";
import { TransactionService } from "../../../services/TransactionService";
import { tryGetOrgId } from "../../../store/orgHelpers";

import { FormField, PillTabs } from "../../../components/ui";
import type { PillTab } from "../../../components/ui";
import ProfitActionModal from "../../../components/modal/modal-profit-action/modal-profit-action";

import "./profit.scss";

type ModalKind = null | "withdraw" | "transfer";
type TabKey = "profit" | "withdrawals" | "transfers";

const TABS: PillTab<TabKey>[] = [
    { key: "profit",      label: "Lucro",                          icon: TrendingUp },
    { key: "withdrawals", label: "Retiradas",                      icon: BanknoteArrowDownIcon },
    { key: "transfers",   label: "Envio para a carteira da empresa", icon: Building2 },
];

// operation_type das transactions da carteira de lucro no otc-core
// (OperationType.PROFIT_WITHDRAW / PROFIT_TRANSFER).
const OPERATION_BY_TAB: Record<Exclude<TabKey, "profit">, string> = {
    withdrawals: "ProfitWithdraw",
    transfers:   "ProfitTransfer",
};

type DateRangeQuery = { date_start?: string; date_end?: string };

// Date (dia local do DatePicker) -> "YYYY-MM-DD" para date_start/date_end.
const toDayParam = (d: Date | null): string | undefined => {
    if (!d) return undefined;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Lucro formatado: FIAT = 4 casas, CRYPTO = 8 casas. Regra local da listagem
// (nao reutilizo formatAssetAmount porque la as casas sao por simbolo).
const formatProfit = (value: unknown, assetType?: string): string => {
    const n = Number(value) || 0;
    const decimals = (assetType ?? "").toUpperCase() === "FIAT" ? 4 : 8;
    return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(n);
};

const Profit = () => {

    const dispatch = useAppDispatch();

    const profitList : Array<any> | null = useAppSelector((s) => s.purchase.profitList);
    const orgWallets : Array<any> | null = useAppSelector((s) => s.wallet.organizationWallets);

    const [openModal, setOpenModal] = useState<ModalKind>(null);

    // fStart/fEnd sao o rascunho dos DatePickers; `range` e o periodo
    // aplicado — so muda no clique em Filtrar e e o que vai pros requests.
    const [fStart, setFStart] = useState<Date | null>(null);
    const [fEnd, setFEnd]     = useState<Date | null>(null);
    const [range, setRange]   = useState<DateRangeQuery>({});

    useEffect(() => {
        dispatch(fetchWalletsByOrganizations());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchProfits(range));
    }, [dispatch, range]);

    const profitWallet = useMemo(() => {
        if (!orgWallets) return null;
        return orgWallets.find((w: any) => w.nickname?.toLowerCase() === "profit") ?? null;
    }, [orgWallets]);

    const profitBalance = Number(profitWallet?.balance ?? 0);

    const [tab, setTab] = useState<TabKey>("profit");

    // Transactions da aba ativa (retiradas ou envios para a empresa).
    const orgId = useAppSelector(tryGetOrgId);
    const [movements, setMovements]               = useState<Array<any>>([]);
    const [loadingMovements, setLoadingMovements] = useState(false);

    const loadMovements = useCallback(async () => {
        if (!orgId || tab === "profit") return;
        setLoadingMovements(true);
        setMovements([]);
        try {
            const resp: any = await TransactionService.searchByOperationType(orgId, OPERATION_BY_TAB[tab], range);
            setMovements(resp?.data ?? []);
        } catch (e) {
            console.error(e);
            setMovements([]);
        } finally {
            setLoadingMovements(false);
        }
    }, [orgId, tab, range]);

    useEffect(() => { loadMovements(); }, [loadMovements]);

    // Listas ja chegam filtradas pelo backend (date_start/date_end).
    const profitRows = profitList ?? [];

    // Apos sacar/enviar, recarrega a lista de movimentos (o saldo ja e
    // recarregado pelo proprio thunk).
    const runAndReload = (thunk: any) => async (value: number) => {
        const ok = await dispatch(thunk(value));
        if (ok) loadMovements();
        return ok;
    };

    // Objeto novo a cada clique: refaz o request mesmo com o mesmo periodo.
    const applyFilters = () => {
        setRange({ date_start: toDayParam(fStart), date_end: toDayParam(fEnd) });
    };
    const invalidRange = !!fStart && !!fEnd && fStart > fEnd;

    return (
        <div className="profit-wrapper">

            <h1 className="page-title">
                Caixa
                <small>Lucro, retiradas e envios da carteira de lucro.</small>
            </h1>

            <div className="profit-total-card">
                <div className="profit-total-info">
                    <span className="label">
                        <PiggyBank size={16} /> Saldo da carteira de lucro
                    </span>
                    <strong className="value num">
                        {formatCurrencyBRL(profitBalance)}
                    </strong>
                </div>

                <div className="profit-total-actions">
                    <button
                        className="btn-pill ghost"
                        disabled={profitBalance <= 0}
                        onClick={() => setOpenModal("withdraw")}
                    >
                        <BanknoteArrowDownIcon size={16} /> Sacar
                    </button>
                    <button
                        className="btn-pill primary"
                        disabled={profitBalance <= 0}
                        onClick={() => setOpenModal("transfer")}
                    >
                        <BanknoteArrowUpIcon size={16} /> Adicionar a carteira da empresa
                    </button>
                </div>
            </div>

            <PillTabs
                className="profit-tabs"
                tabs={TABS}
                value={tab}
                onChange={setTab}
                ariaLabel="Movimentos da carteira de lucro"
                collapseOnMobile
            />

            <div className="profit-filters">
                <FormField label="De" htmlFor="pf-start">
                    <DatePicker
                        id="pf-start"
                        selected={fStart}
                        onChange={(d: any) => setFStart(d)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Inicio"
                        isClearable
                        showIcon
                    />
                </FormField>
                <FormField label="Ate" htmlFor="pf-end">
                    <DatePicker
                        id="pf-end"
                        selected={fEnd}
                        onChange={(d: any) => setFEnd(d)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Fim"
                        isClearable
                        showIcon
                    />
                </FormField>
                <button className="btn-pill primary" onClick={applyFilters} disabled={invalidRange}>
                    <Search size={16} /> Filtrar
                </button>
            </div>

            {tab === "profit" ? (
                <div className="list-items-wrapper">

                    <div className="header">
                        <span>Data</span>
                        <span>Lucro</span>
                    </div>

                    <div className="items-list">
                        {profitRows.map((profit: any, index: number) => (
                            <div className="item" key={index}>
                                <span>{profit.updatedAt}</span>
                                <span className="currency">
                                    {profit.asset.symbol} {formatProfit(profit.profit, profit.asset.type)}
                                </span>
                            </div>
                        ))}
                        {profitRows.length === 0 && (
                            <div className="item empty">
                                <span>Sem lucros no periodo selecionado.</span>
                                <span></span>
                            </div>
                        )}
                    </div>

                </div>
            ) : (
                <div className="list-items-wrapper movements">

                    <div className="header">
                        <span>Data</span>
                        <span>Feito por</span>
                        <span>Valor</span>
                    </div>

                    <div className="items-list">
                        {movements.map((m: any) => (
                            <div className="item" key={m.id}>
                                <span>{new Date(m.createdAt).toLocaleString("pt-BR")}</span>
                                <span>{m.owner?.name ?? m.owner?.login ?? "-"}</span>
                                <span className="currency">- {formatCurrencyBRL(Number(m.value))}</span>
                            </div>
                        ))}
                        {!loadingMovements && movements.length === 0 && (
                            <div className="item empty">
                                <span>
                                    {tab === "withdrawals"
                                        ? "Sem retiradas no periodo selecionado."
                                        : "Sem envios para a carteira da empresa no periodo selecionado."}
                                </span>
                                <span></span>
                                <span></span>
                            </div>
                        )}
                        {loadingMovements && (
                            <div className="item empty">
                                <span>Carregando...</span>
                                <span></span>
                                <span></span>
                            </div>
                        )}
                    </div>

                </div>
            )}

            <ProfitActionModal
                open={openModal === "withdraw"}
                onClose={() => setOpenModal(null)}
                icon={BanknoteArrowDownIcon}
                actionIcon={BanknoteArrowDownIcon}
                title="Sacar da carteira de lucro"
                subtitle="retirar valor do lucro acumulado da empresa"
                actionLabel="Sacar"
                maxValue={profitBalance}
                onConfirm={runAndReload(withdrawFromProfit)}
                successTitle="Saque solicitado"
                successMessage="O saque foi registrado com sucesso."
                errorTitle="Falha no saque"
                errorMessage="Nao foi possivel concluir o saque. Tente novamente."
            />

            <ProfitActionModal
                open={openModal === "transfer"}
                onClose={() => setOpenModal(null)}
                icon={Building2}
                actionIcon={BanknoteArrowUpIcon}
                title="Adicionar a carteira da empresa"
                subtitle="transfere do lucro para o capital de giro da empresa"
                actionLabel="Adicionar"
                maxValue={profitBalance}
                onConfirm={runAndReload(transferProfitToCompany)}
                successTitle="Valor transferido"
                successMessage="O valor foi somado a carteira da empresa."
                errorTitle="Falha na transferencia"
                errorMessage="Nao foi possivel concluir a transferencia. Tente novamente."
            />

        </div>
    );

}

export default Profit;
